/**
 * Tiny localStorage-backed JSON store.
 * --------------------------------------
 * One place for the read/write/guard boilerplate that several features need
 * (case navigation memory, case filters, …). Each caller owns its own key and
 * the shape it stores; this just handles JSON (de)serialization and the
 * "localStorage might be unavailable / SSR" edge cases.
 *
 *   const store = createPersistedJson('myKey', {})
 *   const data = store.read()          // parsed value, or a fresh fallback
 *   store.write({ ...data, foo: 1 })   // serialize + persist (best-effort)
 *
 * @param {string} key                 localStorage key.
 * @param {object|Array} [fallback={}]  Value returned when nothing is stored
 *                                      or parsing fails. A *copy* is returned
 *                                      each time so callers can't mutate it.
 */
export function createPersistedJson(key, fallback = {}) {
  const freshFallback = () => JSON.parse(JSON.stringify(fallback))

  return {
    read() {
      if (typeof window === 'undefined') return freshFallback()
      try {
        const raw = window.localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : null

        return parsed ?? freshFallback()
      } catch {
        return freshFallback()
      }
    },

    write(value) {
      if (typeof window === 'undefined') return
      try {
        window.localStorage.setItem(key, JSON.stringify(value))
      } catch {
        // localStorage unavailable (private mode / quota) — non-fatal.
      }
    },
  }
}
